<template>
    <v-container fluid>
        <v-card>
            <v-overlay :value="loading" absolute>
                <div class="text-center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        :size="50"
                        class="my-4"
                    ></v-progress-circular>
                    <p class="display-1">{{ $t("Loading") }}</p>
                </div>
            </v-overlay>
            <v-card-title align-center class="py-3">{{ title }}</v-card-title>
            <v-card-text class="px-5">
                <div class="alerts_header-buttons">
                    <v-menu bottom right>
                        <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip top>
                                <template v-slot:activator="{ on: tooltip}">
                                    <v-btn text small v-bind="attrs" v-on="{ ...tooltip, ...menu }" color="blue-grey">
                                        <v-icon large>mdi-history</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('Versions') }}</span>
                            </v-tooltip>
                        </template>
                        <v-list>
                            <v-list-item v-for="(item, i) in versionList" :key="i" @click="onVersionChange(item)">
                                <v-list-item-title>{{ `v${item.text}` }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn color="secundary" @click.prevent="close()">
                        {{ $t("Cancel") }}
                    </v-btn>
                    <v-btn color="primary" @click.prevent="saveAndExit">
                        {{ $t("Save") }}
                    </v-btn>
                </div>
                <v-row v-if="alerts.length == 0" justify="center" align="center">
                    <v-col cols="2" justify="center" align="center">
                        <v-btn
                            tile
                            color="primary"
                            prepend-icon="mdi-plus"
                            outlined
                            @click="addAlert()"
                        >
                        {{ $t("Add") }}
                        </v-btn>
                    </v-col>
                    </v-row>
                    <div v-for="(item, index) in alerts" :key="item.uuid">
                    <v-row align="center">
                        <v-col cols="auto">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    tile
                                    large
                                    color="error"
                                    icon
                                    @click="removeAlert(item.uuid)"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                                </template>
                                <span>{{ $t("Delete") }}</span>
                            </v-tooltip>
                        </v-col>
                        <v-col cols="3">
                            <v-text-field
                                readonly
                                v-model="item.displayConditionStr"
                                :label="$t('Condition')"
                                :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
                                @click:append="editCondition(item)"
                                :error-messages="conditionErrors(index)"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="2">
                            <v-select
                                :label="$t('Roles')"
                                v-model="item.roleTypes"
                                :items="rolesList"
                                item-text="text"
                                item-value="id"
                                multiple
                                :error-messages="roleErrors(index)"
                            ></v-select>
                        </v-col>
                        <v-col>
                            <v-select
                                :label="$t('Frequency')"
                                v-model="item.triggerId"
                                :items="triggerList"
                                item-text="text"
                                item-value="id"
                                :error-messages="triggerErrors(index)"
                            ></v-select>
                        </v-col>
                        <v-col>
                            <v-btn color="blue darken-4" outlined @click="editActions(item)">
                                {{ $t("Actions") }}
                                <v-icon v-if="item.actions.length > 0" right> mdi-pencil </v-icon>
                                <v-icon v-if="item.actions.length == 0" right> mdi-plus </v-icon>
                            </v-btn>
                        </v-col>
                        <v-col>
                            <v-switch
                                v-model="item.notifyByEmail"
                                :label="$t('NotifyByEmail')"
                            ></v-switch>
                        </v-col>
                        <v-col cols="1">
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                tile
                                large
                                color="primary"
                                icon
                                @click="addAlert()"
                            >
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                            </template>
                            <span>{{ $t("Add") }}</span>
                        </v-tooltip>
                        </v-col>
                    </v-row>
                    </div>
                    <SearchCriteriaBuilderModal
                    :ref="conditionModalKey"
                    :contentTypeIDs="[contentType.id]"
                    @on-condition-saved="onConditionChanged"
                    />
                    <ActionsModal
                    :ref="actionsModalKey"
                    :contentTypeId="contentType.id"
                    :contentTypeFields="contentType.fields"
                    @on-saved="onActionsSaved"
                    />
            </v-card-text>
        </v-card>
    </v-container>
</template>
<script>
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import Utils from '@/ecm/common/Utils';
import ActionsModal from '@/components/administration/contentType/alerts/ActionsModal.vue';
import ContentType from '@/ecm/models/contentType/ContentType';
import ContentTypeField from '@/ecm/models/contentType/ContentTypeField';
import ContentTypeAlert from '@/ecm/models/contentType/alerts/ContentTypeAlert';
import ContentTypeService from '@/services/ContentTypeService';
import ContentTypeAlertsTriggers from '@/ecm/variables/ContentTypeAlertsTriggers';
import RoleService from '@/services/RoleService';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
    name: 'Alerts',
    components: {
        SearchCriteriaBuilderModal,
        ActionsModal,
    },
    async mounted() {
        this.loading = true;
        await this.loadContentTypeData();
        await Promise.all([this.getAlerts(), this.getVersions(), this.getFields(), this.getRoles()]);
        this.loading = false;
        this.$v.$reset();
    },
    async beforeRouteLeave(to, from, next) {
        const isDirty = JSON.stringify(this.alerts) !== this.originalAlerts;
        if (isDirty) {
            try {
                const res = await this.unsavedChangesMessageConfirm();
                if (res === 1) {
                    await this.saveAndExit();
                }
                return next();
            } catch (error) {
                console.error(`Error while saving alert: ${error}`);
                return next(false);
            }
        }
        next();
    },
    data() {
        return {
            page: {
                title: this.$t('Alerts'),
            },
            contentType: new ContentType(),
            conditionModalOpen: false,
            triggerList: [
                {
                    id: ContentTypeAlertsTriggers.Periodically,
                    text: this.$t('Periodically'),
                },
                {
                    id: ContentTypeAlertsTriggers.OnDocumentCreation,
                    text: this.$t('NewDocument'),
                },
                {
                    id: ContentTypeAlertsTriggers.OnNewDocumentVersion,
                    text: this.$t('NewVersion'),
                },
            ],
            currentItem: new ContentTypeAlert(),
            contentTypeRequiredFieldsCodes: [],
            alerts: [],
            originalAlerts: [],
            versionList: [],
            rolesList: [],
            conditionModalKey: 'conditionModalKey',
            actionsModalKey: 'actionModalKey',
            loading: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        alerts: {
            $each: {
                conditionStr: { required },
                roleTypes: {
                    required: requiredIf(function (model) {
                        return model.actions.length == 0;
                    }),
                },
                triggerId: { required },
            },
        },
    },
    computed: {
        title() {
            return `${this.page.title} - ${this.contentType.name} ${this.contentType.version}`;
        },
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
        actionsModalInstance() {
            return this.$refs[this.actionsModalKey];
        },
    },
    methods: {
        async loadContentTypeData() {
            const id = this.$route.params.id;
            const ctData = await ContentTypeService.GetNameAndVersions(id);

            if (!ctData) {
                Utils.toast.error(`${this.$t('ItemNotFoundException')} (${this.$t('ContentType')}: ${id})`);
                this.close();
            }
            this.contentType = new ContentType();
            this.contentType.id = id;
            this.contentType.name = ctData.name;
            this.contentType.version = `v${ctData.currentVersion}`;
            this.versionList = ctData.versions;
        },
        async unsavedChangesMessageConfirm() {
            return new Promise((resolve) => {
                this.$confirm(this.$t('UnsavedChangesWarning'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Save'),
                    buttonFalseText: this.$t('Cancel')
                }).then((res) => {
                    resolve(res);
                });
            });
        },
        conditionErrors(index) {
            const errors = [];
            if (!this.$v.alerts.$each[index].conditionStr.$dirty) return errors;
            !this.$v.alerts.$each[index].conditionStr.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        roleErrors(index) {
            const errors = [];
            if (!this.$v.alerts.$each[index].roleTypes.$dirty) return errors;
            !this.$v.alerts.$each[index].roleTypes.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        triggerErrors(index) {
            const errors = [];
            if (!this.$v.alerts.$each[index].triggerId.$dirty) return errors;
            !this.$v.alerts.$each[index].triggerId.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        editCondition(item) {
            this.currentItem = item;
            this.conditionModalInstance.edit(item.conditionStr);
        },
        onConditionChanged(condition) {
            this.currentItem.conditionStr = condition.conditionStr;
            this.currentItem.displayConditionStr = condition.displayConditionStr;
        },
        async editActions(item) {
            this.currentItem = item;
            await this.actionsModalInstance.edit(item.actions);
        },
        onActionsSaved(actions) {
            this.currentItem.actions = actions;
        },
        async onVersionChange(version) {
            if (this.contentType.id === version.id) return;
            this.loading = true;
            const isDirty = JSON.stringify(this.alerts) !== this.originalAlerts;
            if (isDirty) {
                const res = await this.unsavedChangesMessageConfirm();
                if (res === 1) {
                    await this.saveAlerts();
                }
                this.$v.$reset();
            }
            this.contentType.id = parseInt(version.id);
            this.contentType.version = `v${version.text}`;
            await Promise.all([this.getAlerts(), this.getFields()]);
            this.loading = false;
        },
        async getAlerts() {
            const alerts = await ContentTypeService.getAlerts(this.contentType.id);
            this.alerts = alerts.map((dto) => new ContentTypeAlert(dto));
            this.originalAlerts = JSON.stringify(this.alerts);
        },
        async getVersions() {
            const versions = await ContentTypeService.getVersions(this.contentType.id);
            this.versionList = versions;
        },
        async getFields() {
            const fields = await ContentTypeService.getFields([this.contentType.id]);
            this.contentType.fields = fields.map(function (dto) {
                return new ContentTypeField(dto);
            });
        },
        async getRoles() {
            const roles = await RoleService.getList();
            this.rolesList = roles;
        },
        async saveAlerts() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            var requiredFieldCodes = this.contentType.fields.filter((field) => field.required).map((item) => item.code);
            if (requiredFieldCodes.length > 0) {
                var alertsWithEmptyRequiredFields = this.alerts
                    .filter((alert) =>
                        alert.actions.some(
                            (field) => field.defFieldCode != '' &&
                                requiredFieldCodes.includes(
                                    field.defFieldCode
                                ) && field.value == ''
                        )
                    )
                    .map((item) => item.displayConditionStr);
                if (alertsWithEmptyRequiredFields.length > 0) {
                    var errorMessage = alertsWithEmptyRequiredFields.join(',');
                    Utils.toast.error(
                        this.$t('PendingNullFieldsAlerts') + errorMessage
                    );
                    return;
                }
            }
            this.loading = true;
            var alerts = this.alerts.map((item) => item.toDto());
            try {
                await ContentTypeService.saveAlerts(this.contentType.id, alerts);
                Utils.toast.success(this.$t('SavedSuccessfully'));
                this.originalAlerts = JSON.stringify(this.alerts);
                this.$v.$reset();
            } catch (error) {
                console.error('Error saving content type alerts:', error);
            } finally {
                this.loading = false;
            }
        },
        addAlert() {
            this.alerts.push(new ContentTypeAlert());
        },
        removeAlert(uuid) {
            this.alerts = this.alerts.filter((item) => item.uuid !== uuid);
        },
        close() {
            this.$router.push({ name: 'ContentTypeList' });
        },
        async saveAndExit() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            await this.saveAlerts();
            this.close();
        }
    },
};
</script>
<style>
.alerts_header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 5px 0px;
}
</style>
